import React, { useMemo, useState } from 'react';
import { JOB_LABEL, assigneesOf, byJob, cn } from './model/derive';
import { defaultReceiverFor } from './model/actions';
import {
  KST,
  formatClock,
  formatDue,
  fromInputValue,
  toInputValue,
  tzShortName,
} from './model/time';
import { useWorkboardCtx } from './model/useWorkboard';
import { Field, Modal, ModalButton, Select, TextInput } from './Modal';
import TaskListEditor, { cleanItems } from './TaskListEditor';
import TaskListGroups from './TaskListGroups';
import { CheckRow } from './TaskActionModals';
import FileDrop, { uploadAttachments } from './FileDrop';
import { baseItemsOf, handoffTypeByJobs, listOf } from './model/taskList';
import { SKILL_TAGS } from './model/skillTags';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';

/**
 * 태스크 추가 — 기획자의 진행 중 배정 (MVP #1 · §5 · Task Flows W5_AddTaskModal).
 *   Title · Assignees · Start(선택) · Due · Receiver · Task List(기본 항목 + 과제 항목) · Skill tags · Files
 * 마감은 지금 이후 ~ 프로젝트 종료 전까지만 고를 수 있다. 시작은 프로젝트 시작 ~ 마감 전 (타임라인 막대의 시작).
 * 팀 시작 전(리더 단독 1시간)에 보내면 팀원 알림은 팀 시작 때 간다 — 안내 줄과 토스트로 알린다.
 *
 * task 를 넘기면 고치기 — PM 이 만든 태스크를 받는 사람이 승인하기 전까지.
 * 담당자 · 받는 사람 · 파일은 그대로 (Reassign · Change receiver 로 바꾼다). 검토 중이면 Task List 도 그대로.
 */

/** PM 이 Add task 로 만든(코드 없음) 태스크 — 승인 전이면 고칠 수 있다 */
export const canEditTask = (task) =>
  !task.code &&
  ['assigned', 'in_progress', 'declined', 'rework_requested', 'submitted'].includes(task.state);

const HOUR = 60 * 60 * 1000;
const pickable = (m) => m.status === 'active' && m.role !== 'npc' && m.job !== 'marketer';

const AddTaskModal = ({ onClose, task: editing }) => {
  const { data, dispatch, now, displayTz, memberById, toast, live, slug } = useWorkboardCtx();
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState('');
  const members = useMemo(() => [...memberById.values()].sort(byJob), [memberById]);
  const npc = members.find((m) => m.role === 'npc');
  const [title, setTitle] = useState(editing?.title ?? '');
  // 담당자 여러 명 — 고른 순서대로, 첫 사람이 대표(받는 사람 규칙의 기준)
  const [assigneeIds, setAssigneeIds] = useState(editing ? assigneesOf(editing) : []);
  const [receiverPick, setReceiverPick] = useState(editing?.receiverId ?? '');
  const [start, setStart] = useState(
    editing?.plannedStartAt ? toInputValue(new Date(editing.plannedStartAt), displayTz) : '',
  );
  const [due, setDue] = useState(
    editing ? toInputValue(new Date(editing.dueAt), displayTz) : '',
  );
  // 고칠 때는 과제 항목만 (기본 항목은 유형에서 다시 붙는다)
  const editingItems = editing ? listOf(editing, memberById).items : [];
  const [taskList, setTaskList] = useState(editingItems.length ? editingItems : ['']);
  const [tags, setTags] = useState(editing?.skillTags ?? []);
  // 받는 사람이 검토 중이면 Task List 는 바꾸지 않는다
  const listLocked = editing?.state === 'submitted';
  // 진행 중이면 시작 · 마감은 그대로 (타임라인 조정과 같은 규칙)
  const timeLocked = editing?.state === 'in_progress';
  const [files, setFiles] = useState([]);

  const assignee = memberById.get(assigneeIds[0]);
  const receiverOptions = [
    ...members.filter((m) => pickable(m) && !assigneeIds.includes(m.id)),
    ...(npc ? [npc] : []),
  ];
  const ruleReceiver = defaultReceiverFor(
    assignee,
    members.filter((m) => m.role === 'npc' || (pickable(m) && !assigneeIds.includes(m.id))),
  );
  const receiver =
    receiverOptions.find((m) => m.id === receiverPick) ?? (assignee ? ruleReceiver : null);
  const type = handoffTypeByJobs(
    assignee?.job,
    receiver && receiver.role !== 'npc' ? receiver.job : null,
    false,
  );
  const base = baseItemsOf(type);
  const dueAt = fromInputValue(due, displayTz);
  const projectEnd = new Date(data.project.endAt);
  const projectStart = new Date(data.project.startAt);
  // 고칠 때 마감을 그대로 두면 지난 시각이어도 된다
  const dueKept = editing && dueAt && dueAt.getTime() === new Date(editing.dueAt).getTime();
  const dueOk = dueAt && (dueKept || dueAt > now) && dueAt <= projectEnd;
  const startAt = start ? fromInputValue(start, displayTz) : null;
  const startOk = !start || (startAt && startAt >= projectStart && (!dueAt || startAt < dueAt));
  const taskListItems = cleanItems(taskList).filter((x) => !base.includes(x));
  const tagOptions = SKILL_TAGS[assignee?.job] ?? [];
  const tagsOk = tags.some((t) => tagOptions.includes(t));
  const listOk = base.length + taskListItems.length > 0;
  const canSubmit = title.trim() && assignee && dueOk && startOk && receiver && listOk && tagsOk;
  // 팀 시작(리더 시작 + 1시간) 전이면 팀원 알림은 그때 간다
  const teamStart = new Date(
    data.project.teamStartAt ?? new Date(data.project.startAt).getTime() + HOUR,
  );
  const beforeTeamStart = now < teamStart;
  const tz = tzShortName(displayTz, now);

  const toggleAssignee = (id) => {
    const next = assigneeIds.includes(id)
      ? assigneeIds.filter((x) => x !== id)
      : [...assigneeIds, id];
    setAssigneeIds(next);
    if (next[0] !== assigneeIds[0]) setTags([]);
  };
  const toggleTag = (t) =>
    setTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  const save = () => {
    if (!canSubmit) return;
    dispatch({
      type: 'editTask',
      taskId: editing.id,
      task: {
        title: title.trim(),
        startAt: startAt ? startAt.toISOString() : null,
        dueAt: dueAt.toISOString(),
        taskList: listLocked ? editingItems : taskListItems,
        skillTags: tags.filter((t) => tagOptions.includes(t)),
      },
      at: now.toISOString(),
    });
    toast?.({ title: 'Task updated', sub: 'The assignees get notified' });
    onClose();
  };

  const submit = async () => {
    if (editing) {
      save();
      return;
    }
    if (!canSubmit || uploading) return;
    // 실제 보드는 S3 에 먼저 올리고 키를 보낸다. 목 보드는 브라우저 안 임시 주소
    let attachments;
    setUploading(true);
    setFileError('');
    try {
      attachments = await uploadAttachments({ live, slug }, files);
    } catch {
      setUploading(false);
      setFileError('Couldn’t upload the files. Check your connection and try again.');
      return;
    }
    setUploading(false);
    dispatch({
      type: 'addTask',
      task: {
        id: `t-${Date.now()}`,
        title: title.trim(),
        assigneeId: assigneeIds[0],
        assigneeIds,
        receiverId: receiver.id,
        dueAt: dueAt.toISOString(),
        startAt: startAt ? startAt.toISOString() : null,
        plannedStartAt: startAt ? startAt.toISOString() : null,
        state: 'assigned',
        isBlocked: false,
        assignedAt: (beforeTeamStart ? teamStart : now).toISOString(),
        taskList: taskListItems,
        taskSources: taskListItems.map(() => 'pm'),
        skillTags: tags.filter((t) => tagOptions.includes(t)),
        files: attachments,
        contributors: [],
        predecessorIds: [],
      },
    });
    toast?.({
      title: 'Task sent',
      sub: beforeTeamStart
        ? `No notifications before team start (${formatClock(teamStart, KST)} KST)`
        : `${assigneeIds.map((id) => memberById.get(id)?.name).join(', ')} will see it in My tasks`,
    });
    onClose();
  };

  return (
    <Modal
      title={editing ? 'Edit task' : 'Add task'}
      subtitle={[data.project.title, data.project.teamName].filter(Boolean).join(' · ')}
      onClose={onClose}
      width='max-w-[560px]'
      footer={
        <>
          {!canSubmit && (
            <span className='mr-auto text-[13px] text-text-secondary'>
              {!title.trim()
                ? 'Add a title'
                : !assignee
                  ? 'Pick an assignee'
                  : !startOk
                    ? 'Pick a start before the due time'
                    : !dueOk
                    ? 'Pick a due time'
                    : !receiver
                      ? 'Pick a receiver'
                      : !listOk
                        ? 'Add at least one Task List item'
                        : !tagsOk
                          ? 'Pick at least one skill tag'
                          : ''}
            </span>
          )}
          <ModalButton onClick={onClose}>Cancel</ModalButton>
          <ModalButton primary disabled={!canSubmit || uploading} onClick={submit}>
            {editing ? 'Save changes' : uploading ? 'Uploading…' : 'Add task'}
          </ModalButton>
        </>
      }
    >
      <Field label='Title' required htmlFor='task-title'>
        <TextInput
          id='task-title'
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field>

      {editing ? (
        <Field label='Assignees · Receiver' hint='Use Reassign or Change receiver to change them.'>
          <p className='text-[14px] text-text-primary'>
            {assigneeIds.map((id) => memberById.get(id)?.name ?? 'Unknown').join(', ')} →{' '}
            {receiver?.role === 'npc' ? 'Team lead Kim' : (receiver?.name ?? '—')}
          </p>
        </Field>
      ) : (
      <fieldset className='flex flex-col gap-1'>
        <legend className='mb-1 text-[13px] font-medium text-text-primary'>
          Assignees<span className='text-functional-failed03'>*</span>
        </legend>
        <span className='mb-1 text-[12px] text-text-secondary'>
          Pick one or more. Any assignee can accept and submit.
        </span>
        <div id='task-assignee' className='flex flex-col'>
          {members.filter(pickable).map((m) => (
            <CheckRow
              key={m.id}
              label={`${m.name} · ${JOB_LABEL[m.job] ?? m.job}`}
              checked={assigneeIds.includes(m.id)}
              onChange={() => toggleAssignee(m.id)}
            />
          ))}
        </div>
      </fieldset>
      )}

      <Field
        label='Start'
        optional
        htmlFor='task-start'
        hint={
          start && !startOk
            ? 'Pick a time after the project start and before the due time.'
            : timeLocked
              ? 'The task is in progress, so its start and due can’t change.'
              : 'When work should begin. It sets where the task starts on the timeline.'
        }
      >
        <div className='relative'>
          <TextInput
            id='task-start'
            type='datetime-local'
            value={start}
            disabled={timeLocked}
            min={toInputValue(projectStart, displayTz)}
            max={toInputValue(projectEnd, displayTz)}
            onChange={(e) => setStart(e.target.value)}
            className={cn('pr-24', start && !startOk && 'border-functional-failed03')}
          />
          <span className='pointer-events-none absolute right-10 top-1/2 -translate-y-1/2 text-[13px] text-text-secondary'>
            {tz}
          </span>
        </div>
      </Field>

      <Field
        label='Due'
        required
        htmlFor='task-due'
        hint={
          due && !dueOk
            ? `Pick a time between now and the project end (${formatDue(projectEnd, displayTz)} ${tz}).`
            : null
        }
      >
        <div className='relative'>
          <TextInput
            id='task-due'
            type='datetime-local'
            value={due}
            disabled={timeLocked}
            min={toInputValue(now, displayTz)}
            max={toInputValue(projectEnd, displayTz)}
            onChange={(e) => setDue(e.target.value)}
            className={cn('pr-24', due && !dueOk && 'border-functional-failed03')}
          />
          <span className='pointer-events-none absolute right-10 top-1/2 -translate-y-1/2 text-[13px] text-text-secondary'>
            {tz}
          </span>
        </div>
      </Field>

      {!editing && (
      <Field
        label='Receiver'
        required
        htmlFor='task-receiver'
        hint={
          assignee
            ? 'Set by the brief rule. You can pick someone else. Assignees can’t be the receiver.'
            : 'Pick an assignee first.'
        }
      >
        <Select
          id='task-receiver'
          value={receiver?.id ?? ''}
          disabled={!assignee}
          onChange={(e) => setReceiverPick(e.target.value)}
        >
          {!assignee && <option value=''>Select a person</option>}
          {receiverOptions.map((m) => (
            <option key={m.id} value={m.id}>
              {m.role === 'npc' ? 'Team lead Kim' : `${m.name} · ${JOB_LABEL[m.job] ?? m.job}`}
              {m.id === ruleReceiver?.id ? ' · brief rule' : ''}
            </option>
          ))}
        </Select>
      </Field>
      )}

      <Field
        label='Task List'
        required
        htmlFor='task-list'
        hint={
          listLocked
            ? 'The receiver is reviewing it, so the Task List can’t change now.'
            : 'Base items come from the handoff type. Add task items below · Enter adds the next. The receiver checks each item ✓ or ✗ at handoff.'
        }
      >
        <div className='flex flex-col gap-3'>
          {base.length > 0 && <TaskListGroups type={type} base={base} items={[]} />}
          {listLocked ? (
            <TaskListGroups type={type} base={[]} items={editingItems} />
          ) : (
          <TaskListEditor
            id='task-list'
            items={taskList}
            onChange={setTaskList}
            startAt={base.length}
            placeholder={base.length ? 'Add a task item (optional)' : undefined}
            ariaLabel='Task items'
          />
          )}
        </div>
      </Field>

      <fieldset className='flex flex-col gap-1'>
        <legend className='mb-1 text-[13px] font-medium text-text-primary'>
          Skill tags<span className='text-functional-failed03'>*</span>
        </legend>
        <span className='mb-1 text-[12px] text-text-secondary'>
          {assignee
            ? 'Pick one or more. They’re saved with the approved deliverable.'
            : 'Pick an assignee first.'}
        </span>
        <div className='flex flex-wrap gap-1.5'>
          {tagOptions.map((t) => {
            const on = tags.includes(t);
            return (
              <button
                key={t}
                type='button'
                aria-pressed={on}
                onClick={() => toggleTag(t)}
                className={cn(
                  'h-8 rounded-full px-3 text-[13px] font-medium',
                  on
                    ? 'bg-surface-green10 text-green-deep04 shadow-[inset_0_0_0_1px_#1dc37b]'
                    : 'bg-grayscale-gray00 text-text-secondary shadow-[inset_0_0_0_1px_#d5d2cd] hover:text-text-primary',
                )}
              >
                {on && <CheckIcon aria-hidden className='-ml-0.5 mr-1 inline h-3.5 w-3.5 align-[-2px]' />}
                {t}
              </button>
            );
          })}
        </div>
      </fieldset>

      {!editing && (
      <Field label='Files' optional hint='Up to 10 files · 20 MB each'>
        <FileDrop files={files} onChange={setFiles} disabled={uploading} />
        {fileError && (
          <p role='alert' className='text-[12px] text-functional-failed04'>
            {fileError}
          </p>
        )}
      </Field>
      )}

      {beforeTeamStart && !editing && (
        <p className='rounded-lg bg-grayscale-gray01 px-3 py-2.5 text-[13px] leading-[150%] text-text-primary'>
          ⓘ Members aren’t notified until the team joins at {formatClock(teamStart, KST)} KST.
        </p>
      )}
    </Modal>
  );
};

export default AddTaskModal;
